






































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import MiraLandingFiguresDialogComponent from "@/components/mira/MiraLandingFiguresDialog.vue";
import MiraScreenHeaderComponent from "@/components/mira/MiraScreenHeader.vue";
import MiraSectionCardComponent from "@/components/mira/MiraSectionCard.vue";
import {
  MIRA_ALL_CLIENTS_SCOPE,
  MIRA_BRANCH_OTHER,
  MIRA_PERIOD_ROLLING_12,
  MiraBranchDocument,
  MiraSection,
  MiraSectionDocument,
} from "@/data/models/Mira";
import {
  isMiraGroupMaster,
  loadBranchSection,
  loadInsurerSection,
  loadGroupViewSection,
  loadMarketSection,
  loadMiraSections,
} from "@/data/repository/MiraRepository";
import router from "@/router/router";
import { MiraComparisonModule } from "@/store/modules/MiraComparisonModule";
import { MiraPeriodModule } from "@/store/modules/MiraPeriodModule";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK } from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import {
  MIRA_COMPARISON_BRANCHES,
  MIRA_COMPARISON_MARKET,
  MIRA_COMPARISON_MEMBERS,
  MIRA_COMPARISON_NONE,
  MiraComparisonMode,
} from "@/utils/MiraComparisonView";
import { miraPeriodName, periodRange } from "@/utils/MiraDataQuality";
import { MiraTile, publishedMetric } from "@/utils/MiraDataQualityView";
import { setMiraToolbarTitle } from "@/utils/MiraEdition";
import { mayEditMiraLandingChoice, miraLandingChoice } from "@/utils/MiraLandingChoice";
import { withMarketOfOwnKind } from "@/utils/MiraMarketKind";
import {
  effectiveMarketCountry,
  marketCountryOf,
  marketCountryOption,
  listedMarketCountries,
  marketCountryOptionName,
  marketDocumentForCountry,
  withMarketCountryOptions,
} from "@/utils/MiraMarketCountries";
import { miraMarketCountriesAccess } from "@/permissions/UserPermissions";
import {
  MIRA_SECTION_ICONS,
  MIRA_SECTIONS_WITH_SCREEN,
  MiraSectionAvailability,
  cardAvailability,
  effectivePeriod,
  landingDownwardOptions,
  landingUpwardOptions,
  landingFigures,
  landingPeriods,
  landingSections,
  sectionAvailability,
} from "@/utils/MiraOverview";
import { MIRA_SECTION_FORMATTERS, MIRA_SECTION_TILES, miraViewContext } from "@/utils/MiraSectionTiles";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { getBranchesOfClient, getClientId } from "@/data/repository/ClientRepository";
import {
  MiraComparisonCandidate,
  MiraComparisonKindOption,
  MiraSavedComparison,
  MiraSavedComparisonKind,
} from "@/data/models/MiraSavedComparison";
import {
  activeSavedComparisonOf,
  downwardChoiceOf,
  downwardMenuOptionsOf,
  savedComparisonIdOf,
  savedComparisonKindsOf,
  savedComparisonOption,
  savedComparisonsFor,
} from "@/utils/MiraSavedComparisons";
import { getEffectiveUser } from "@/utils/ViewAs";
import { loadMiraMemberRoster } from "@/utils/MiraMemberRoster";

/** The props of one section card. */
interface MiraSectionCardData {
  section: MiraSection;
  icon: string;
  title: string;
  description: string;
  availability: MiraSectionAvailability;
  opens: boolean;
  figures: MiraTile[];
  /** "Markt 29 %" per figure id, with Markt chosen as the comparison (#2610137). */
  marketTexts: Record<string, string>;
}

/** A section's documents for the comparisons, loaded after the cards (#2610137); `null` where absent. */
type MiraSectionDocuments = Partial<Record<MiraSection, MiraSectionDocument | null>>;

/** The route each section with a detail screen opens. */
const SECTION_ROUTES: Partial<Record<MiraSection, string>> = {
  [MiraSection.DATA_QUALITY]: ComponentName.MIRA_DATA_QUALITY,
  [MiraSection.HYDRY]: ComponentName.MIRA_DRYING,
  [MiraSection.DEVICES]: ComponentName.MIRA_DEVICES,
  [MiraSection.BUSINESS_DEVELOPMENT]: ComponentName.MIRA_BUSINESS_DEVELOPMENT,
  [MiraSection.PROCESS_FLOW]: ComponentName.MIRA_PROCESS_FLOW,
  [MiraSection.INSURANCE]: ComponentName.MIRA_INSURANCE,
};

/**
 * Landing page of Mira, the analytics screens: an overview card per section, over the chosen period.
 *
 * Each card is a square like the Cockpit's tiles (Hans, #2610102): what its section covers, six
 * figures (its screen's first, or the user's own choice, `landingFigures`), and a gear to the
 * section's settings beside how many of its figures the company has. Only a section with a
 * detail screen opens (`MIRA_SECTIONS_WITH_SCREEN`); the others are listed so the page shows the
 * whole product.
 *
 * The app bar carries the screens' period and Vergleich icons (Hans, #2610137), both one choice shared
 * with every Mira screen (`MiraPeriodModule`, `MiraComparisonModule`). With Markt chosen each figure gets
 * its Markt value under it; the other comparisons have no single value for a card, so they only set the
 * choice the screens open with.
 *
 * Gated by `miraAccess` in `hasAccess()`. The title comes from the app bar, so there is deliberately
 * no heading in the body. See `docs/analytics/mira-office-screens.md`.
 */
@Component({
  components: {
    "mira-landing-figures-dialog": MiraLandingFiguresDialogComponent,
    "mira-screen-header": MiraScreenHeaderComponent,
    "mira-section-card": MiraSectionCardComponent,
  },
})
export default class MiraLandingComponent extends Vue {
  @Ref("figuresDialog")
  figuresDialog!: MiraLandingFiguresDialogComponent;

  isLoading = true;
  sections: Partial<Record<MiraSection, MiraSectionDocument>> = {};
  /** The group's Mandanten for "Vergleiche verwalten", loaded when it first opens (`prepareComparisons`). */
  memberCandidates: MiraComparisonCandidate[] = [];
  /**
   * A choice saved on this page, so the card changes at once; the users snapshot brings the same
   * value onto the user record a moment later.
   */
  savedChoices: Partial<Record<MiraSection, string[]>> = {};
  isGroupMaster = false;
  /** The Markt per section, of the client's own kind (`withMarketOfOwnKind`), as the screens read it. */
  marketDocuments: MiraSectionDocuments = {};
  /** The chosen country's Markt per section (#2611022), loaded when an IRES user picks a country. */
  marketCountryDocuments: MiraSectionDocuments = {};
  /** A member's view of its group per section (#2610112 X6d). Never loaded off a group master. */
  groupViewDocuments: MiraSectionDocuments = {};
  /** Whether a section screen has a branch document, so "Standorte" is offered. */
  hasBranches = false;
  /** The business-development per-insurer document (or the first section's), which names the insurers. */
  insurerDocument: MiraBranchDocument | null = null;

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  /** Any section document: they all carry the same periods and the same run date. */
  get anyDocument(): MiraSectionDocument | null {
    const sections = this.sections;
    return Object.values(sections).find((document) => document !== undefined) ?? null;
  }

  /**
   * Every period any section offers with something to show: a card whose section lacks the chosen one says
   * so by its count, and a period no card can fill is not offered (`landingPeriods`).
   */
  get periods(): string[] {
    return landingPeriods(this.sections);
  }

  /** The period the cards show: the one chosen last on any Mira page, or the first offered here. */
  get selectedPeriod(): string {
    return effectivePeriod(MiraPeriodModule.period, this.periods);
  }

  periodName(period: string): string {
    return miraPeriodName(period, (key, params) => this.$t(key, params).toString(), this.$i18n?.locale);
  }

  get periodDescription(): string {
    const selected = this.selectedPeriod;
    const period = Object.values(this.sections).find((document) => document?.periods[selected])?.periods[selected];
    if (!period) {
      return "";
    }

    const { first, last } = periodRange(period.from, period.toExclusive);
    return this.$t("mira.dataQuality.periodRange", [
      this.periodName(selected),
      formatDateToDayMonthYear(first),
      formatDateToDayMonthYear(last),
    ]).toString();
  }

  get isAllClients(): boolean {
    return this.anyDocument?.scope === MIRA_ALL_CLIENTS_SCOPE;
  }

  /** Whether a figure of any card's section has a value in the given documents, in the chosen period. */
  private anyFigureIn(documents: MiraSectionDocuments): boolean {
    const period = this.selectedPeriod;
    const context = miraViewContext(this);
    return landingSections(this.sections, period).some((section) => {
      const other = documents[section]?.periods[period] ?? null;
      const own = this.sections[section]?.periods[period] ?? null;
      return other !== null && MIRA_SECTION_TILES[section](own, null, context).some((tile) => publishedMetric(other, tile.id) !== null);
    });
  }

  /** What the comparisons offered here are decided on: any a section screen can offer. */
  private get comparisonFacts() {
    return {
      allClients: this.isAllClients,
      groupMaster: this.isGroupMaster,
      branches: this.hasBranches,
      insurers: this.insurerDocument !== null,
      groupView: this.anyFigureIn(this.groupViewDocuments),
      market: this.anyFigureIn(this.marketDocuments),
    };
  }

  /**
   * The countries the Markt can be narrowed to, for IRES users only (#2611022): every country any section
   * lists, in IRES Cloud's order (most companies first), since a country is listed only where it has a figure.
   */
  get marketCountries(): string[] {
    const countries = listedMarketCountries(Object.values(this.marketDocuments));
    // No access check without countries (a Markt document from before lists none).
    return countries.length > 0 && miraMarketCountriesAccess(getEffectiveUser()) ? countries : [];
  }

  /** The country of the Markt in effect, as on the screens. */
  get marketCountry(): string | null {
    return effectiveMarketCountry(MiraComparisonModule.marketCountry, this.marketCountries);
  }

  /** The menu upwards with a country's Markt after "Markt" (#2611022). */
  get upwardMenuOptions(): string[] {
    return withMarketCountryOptions(this.upwardOptions, MIRA_COMPARISON_MARKET, this.marketCountries);
  }

  /** The ticked entry of the menu upwards: a country's Markt where one is in effect. */
  get upwardMenuValue(): string {
    const mode = this.upwardMode;
    const country = this.marketCountry;
    return mode === MIRA_COMPARISON_MARKET && country ? marketCountryOption(country) : mode;
  }

  /**
   * The Markt a section's figures read: the chosen country's where one is in effect, else every country's. A
   * section without a Markt for that country shows none, never every country's under the country's name.
   */
  private marketDocumentFor(section: MiraSection): MiraSectionDocument | null {
    return marketDocumentForCountry(this.marketCountry, this.marketCountryDocuments[section], this.marketDocuments[section]);
  }

  /** Loads the chosen country's Markt for every section (#2611022). */
  @Watch("marketCountry")
  async onMarketCountry(country: string | null) {
    if (!country) {
      return;
    }
    const documents: MiraSectionDocuments = {};
    await Promise.all(
      (Object.keys(this.sections) as MiraSection[]).map(async (section) => {
        // Only where the section lists the country: IRES Cloud writes no document for the others.
        const listed = this.marketDocuments[section]?.countries?.includes(country) ?? false;
        documents[section] = listed
          ? withMarketOfOwnKind(await loadMarketSection("projects", section, country), this.sections[section] ?? null)
          : null;
      })
    );
    // A newer choice while this one loaded wins.
    if (this.marketCountry === country) {
      this.marketCountryDocuments = documents;
    }
  }

  /** The comparisons upwards any section screen can offer; the choice is the screens' shared one. */
  get upwardOptions(): MiraComparisonMode[] {
    return landingUpwardOptions(this.comparisonFacts);
  }

  /** The comparisons downwards any section screen can offer. */
  get downwardOptions(): MiraComparisonMode[] {
    return landingDownwardOptions(this.comparisonFacts);
  }

  /** The chosen comparisons where this page offers them, else "Kein Vergleich", as on the screens. */
  get upwardMode(): MiraComparisonMode {
    // Read the reactive values first, then branch (ui-conventions, Reactivity).
    const stored = MiraComparisonModule.upward;
    const options = this.upwardOptions;
    return options.includes(stored) ? stored : MIRA_COMPARISON_NONE;
  }

  get downwardMode(): MiraComparisonMode {
    const stored = MiraComparisonModule.downward;
    const options = this.downwardOptions;
    return options.includes(stored) ? stored : MIRA_COMPARISON_NONE;
  }

  /** What this client compares downwards, and so the kinds of its saved comparisons. */
  get savedComparisonKinds(): MiraSavedComparisonKind[] {
    return savedComparisonKindsOf(this.downwardOptions);
  }

  /** The user's saved comparisons downwards, listed and managed as on the screens. */
  get savedComparisons(): MiraSavedComparison[] {
    return savedComparisonsFor(getEffectiveUser(), getClientId(), this.savedComparisonKinds);
  }

  /**
   * The kinds for "Vergleiche verwalten", each with what it can pick: the Standorte in the client's order, the
   * Mandanten by name once loaded (`prepareComparisons`), the insurers of the per-insurer document by name.
   */
  get comparisonKinds(): MiraComparisonKindOption[] {
    return this.savedComparisonKinds.map((kind) => ({
      kind,
      candidates:
        kind === MIRA_COMPARISON_BRANCHES
          ? getBranchesOfClient().map((branch) => ({ id: branch.id, label: branch.name }))
          : kind === MIRA_COMPARISON_MEMBERS
          ? this.memberCandidates
          : this.insurerCandidates,
    }));
  }

  /** The insurers IRES Cloud has a row for, by name ("Weitere Versicherer" is no insurer to pick). */
  get insurerCandidates(): MiraComparisonCandidate[] {
    const document = this.insurerDocument;
    const ids = new Set<string>();
    Object.values(document?.periods ?? {}).forEach((period) => Object.keys(period.branches ?? {}).forEach((id) => ids.add(id)));
    return [...ids]
      .filter((id) => id !== MIRA_BRANCH_OTHER)
      .map((id) => ({ id, label: document?.labels?.[id] ?? id }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }

  /** Before "Vergleiche verwalten" opens: a group's Mandanten, named (the overview loads no member documents). */
  async prepareComparisons(): Promise<void> {
    if (!this.savedComparisonKinds.some((kind) => kind === MIRA_COMPARISON_MEMBERS) || this.memberCandidates.length > 0) {
      return;
    }
    try {
      const { ids, names } = await loadMiraMemberRoster(this.sections);
      // The master is no Mandant to pick: it has no projects of its own.
      const masterId = getClientId();
      this.memberCandidates = ids
        .filter((id) => id !== masterId)
        .map((id) => ({ id, label: names[id] }))
        .sort((a, b) => a.label.localeCompare(b.label));
    } catch (error) {
      // The group list we cannot read leaves the dialog with no Mandanten to pick, which it says.
    }
  }

  get downwardMenuOptions(): string[] {
    return downwardMenuOptionsOf(this.downwardOptions, this.savedComparisons);
  }

  get downwardMenuValue(): string {
    const saved = activeSavedComparisonOf(MiraComparisonModule.downwardSavedId, this.downwardMode, this.savedComparisons);
    return saved ? savedComparisonOption(saved.id) : this.downwardMode;
  }

  /** An option's name: a country's Markt, a saved comparison's own, else the comparison's. */
  comparisonName(option: string): string {
    const countryName = marketCountryOptionName(option, (key, params) => this.$t(key, params).toString(), this.$i18n?.locale ?? "de");
    if (countryName) {
      return countryName;
    }
    const savedId = savedComparisonIdOf(option);
    if (savedId) {
      return this.savedComparisons.find((comparison) => comparison.id === savedId)?.name ?? "";
    }
    return this.$t(`mira.comparison.modes.${option}`).toString();
  }

  /** "Vergleich: Markt, Standorte im Mandanten" on the header's first line, only when a comparison is on. */
  get comparisonChoiceLabel(): string {
    const names = [this.upwardMenuValue, this.downwardMenuValue]
      .filter((option) => option !== MIRA_COMPARISON_NONE)
      .map((option) => this.comparisonName(option));
    return names.length === 0 ? "" : this.$t("mira.comparison.choice", [names.join(", ")]).toString();
  }

  onPeriodChange(period: string) {
    MiraPeriodModule.setPeriod(period);
  }

  /** Set the screens' shared comparisons; a screen loads what it needs for them when it opens. */
  onUpwardChange(option: string) {
    const country = marketCountryOf(option);
    MiraComparisonModule.setUpward(country ? MIRA_COMPARISON_MARKET : (option as MiraComparisonMode));
    MiraComparisonModule.setMarketCountry(country);
  }

  onDownwardChange(option: string) {
    MiraComparisonModule.setDownward(downwardChoiceOf(option, this.savedComparisons));
  }

  /** "Markt 29 %" for each figure with a Markt value, in the unit its screen writes it in. */
  private marketTextsFor(section: MiraSection, figures: MiraTile[]): Record<string, string> {
    const period = this.selectedPeriod;
    const market = this.marketDocumentFor(section)?.periods[period] ?? null;
    if (this.upwardMode !== MIRA_COMPARISON_MARKET || !market) {
      return {};
    }

    const context = miraViewContext(this);
    const own = this.sections[section]?.periods[period] ?? null;
    const texts: Record<string, string> = {};
    figures.forEach((figure) => {
      const value = publishedMetric(market, figure.id)?.value;
      if (value !== null && value !== undefined) {
        const formatted = MIRA_SECTION_FORMATTERS[section](figure.id, context, own)(value);
        texts[figure.id] = this.$t("mira.market.tileCaption", [formatted]).toString();
      }
    });
    return texts;
  }

  get generatedAtDescription(): string {
    const generatedAt = this.anyDocument?.generatedAt;
    return generatedAt
      ? this.$t("mira.dataQuality.generatedAt", [formatDateToDayMonthYear(new Date(generatedAt))]).toString()
      : "";
  }

  get cards(): MiraSectionCardData[] {
    const sections = this.sections;
    const period = this.selectedPeriod;
    const context = miraViewContext(this);

    return landingSections(sections, period).map((section) => {
      const document = sections[section] as MiraSectionDocument;
      const headline = document.periods[period] ?? null;
      // No comparison period: a card shows values only, the changes are on the screen.
      const tiles = MIRA_SECTION_TILES[section](headline, null, context);
      const figures = landingFigures(tiles, this.choiceFor(section));

      return {
        section,
        icon: MIRA_SECTION_ICONS[section],
        title: this.$t(`mira.sections.${section}`).toString(),
        description: this.$t(`mira.landing.descriptions.${section}`).toString(),
        availability: cardAvailability(tiles, document, period),
        opens: MIRA_SECTIONS_WITH_SCREEN.includes(section),
        figures,
        marketTexts: this.marketTextsFor(section, figures),
      };
    });
  }

  /** "Mira · Aus der Werkstatt · Oktober 2026" (#2610137), again on a language change. */
  @Watch("$i18n.locale")
  setTitle() {
    setMiraToolbarTitle(this, "pages.mira");
  }

  async mounted() {
    this.setTitle();

    this.isGroupMaster = isMiraGroupMaster();

    try {
      this.sections = await loadMiraSections();
    } catch (error) {
      SnackbarModule.showError({ key: "mira.dataQuality.loadError" });
    } finally {
      this.isLoading = false;
    }

    // After the cards, so they never wait on it: only the Vergleich icon and the Markt lines need these.
    void this.loadComparisonDocuments();
  }

  /**
   * The documents the screens read for their comparisons, so this page offers the same ones: the Markt
   * of every section, and off a group master the branch documents (never on Datenqualität) and the
   * member's group view. The repository caches them for the session, so the screens read them again for
   * free; a missing or unreadable one is `null` there.
   */
  async loadComparisonDocuments() {
    const sections = Object.keys(this.sections) as MiraSection[];
    if (this.isAllClients || sections.length === 0) {
      return;
    }

    const market: MiraSectionDocuments = {};
    const groupView: MiraSectionDocuments = {};
    const branches: boolean[] = [];
    const insurers: Partial<Record<MiraSection, MiraBranchDocument | null>> = {};
    await Promise.all(
      sections.map(async (section) => {
        market[section] = withMarketOfOwnKind(await loadMarketSection("projects", section), this.sections[section] ?? null);
        if (section !== MiraSection.DATA_QUALITY) {
          insurers[section] = await loadInsurerSection(section, this.isGroupMaster);
        }
        if (!this.isGroupMaster) {
          groupView[section] = await loadGroupViewSection(section);
          if (section !== MiraSection.DATA_QUALITY) {
            branches.push((await loadBranchSection(section)) !== null);
          }
        }
      })
    );

    this.marketDocuments = market;
    // A country chosen on a Mira screen: its Markt too (#2611022).
    await this.onMarketCountry(this.marketCountry);
    this.groupViewDocuments = groupView;
    this.hasBranches = branches.includes(true);
    // Geschäftsentwicklung names the insurers best (most rows); any section's will do without it.
    this.insurerDocument =
      insurers[MiraSection.BUSINESS_DEVELOPMENT] ?? Object.values(insurers).find((document) => !!document) ?? null;
  }

  /** How many figures across every section wait on the company's data, for the unlock-page link. */
  get waitingCount(): number {
    const sections = Object.values(this.sections);
    // "Alle Mandanten" (#2610111): the instructions belong to one Mandant, so there is no unlock page.
    if (sections.some((document) => document?.scope === MIRA_ALL_CLIENTS_SCOPE)) {
      return 0;
    }
    return sections.reduce((total, document) => {
      // The last 12 months whatever the chosen period: the unlock page this link opens counts those.
      return document ? total + sectionAvailability(document, MIRA_PERIOD_ROLLING_12).waiting : total;
    }, 0);
  }

  onOpen(section: MiraSection) {
    const name = SECTION_ROUTES[section];
    if (name) {
      router.push({ name });
    }
  }

  private choiceFor(section: MiraSection): string[] {
    return this.savedChoices[section] ?? miraLandingChoice(section);
  }

  async onSettings(section: MiraSection) {
    const saved = await this.figuresDialog.show(
      section,
      this.sections,
      this.choiceFor(section),
      mayEditMiraLandingChoice(),
      this.selectedPeriod
    );
    if (saved !== null) {
      this.$set(this.savedChoices, section, saved);
    }
  }

  onShowUnlocks() {
    router.push({ name: ComponentName.MIRA_UNLOCK });
  }
}
